import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Stack } from "@mui/material";
import IconLoader from "@/features/Common/components/IconLoader";
import { panelStyle } from "@/theme";
import ScreenWrapper from "../../Common/ScreenWrapper";
import HeartRateCard from "./InfoCard";
import HeartRateChart, { HeartRateDataPoint } from "./HeartRateChart";
import CurrentHeartRate from "./CurrentHeartRate";
import HereToHelpCard from "../../HereToHelpCard";
import MyHeartRateModal from "./MyHeartRateModal";
import { WellnessScoreStatus } from "../../Common/types";

export interface MyHeartRateScreenProps {
  chartData: HeartRateDataPoint[];
  status: WellnessScoreStatus;
  lastUpdated?: string;
  /** When set, used for InfoCard and gauge instead of deriving from chartData (e.g. from health profile). */
  currentHeartRate?: number;
  onBack?: () => void;
  onOpenManualEntry?: () => void;
}

export default function MyHeartRateScreen({
  chartData,
  status,
  lastUpdated,
  currentHeartRate: currentHeartRateOverride,
  onBack,
  onOpenManualEntry,
}: MyHeartRateScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const fromChart =
    chartData.find((p) => p.isSelected) ?? chartData[chartData.length - 1];
  const heartRateValue = currentHeartRateOverride ?? fromChart?.heartRate ?? 0;

  const handleBack = onBack ?? (() => navigate(-1));
  const metricValue = String(heartRateValue);
  const unit = t("HeartRate.unit");

  return (
    <>
      <ScreenWrapper
        onBack={handleBack}
        title={t("HealthChart.myHeartRateModal.card.title")}
        description={t("HealthChart.myHeartRateModal.card.description")}
        onInfoClick={() => setIsInfoModalOpen(true)}
      >
        <Stack gap={2}>
          <HeartRateCard
            metricValue={metricValue}
            status={status}
            lastUpdated={lastUpdated}
            onManualEntryClick={onOpenManualEntry}
          />
          <HeartRateChart data={chartData} />
          <Box sx={{ ...panelStyle }}>
            <CurrentHeartRate value={heartRateValue} unit={unit} />
          </Box>
          <HereToHelpCard
            title={t("HereToHelpCard.title")}
            description={t("HereToHelpCard.description")}
            button={{
              text: t("HereToHelpCard.button.text"),
              icon: <IconLoader icon="SpeakToADoctorIcon" />,
            }}
          />
        </Stack>
      </ScreenWrapper>

      <MyHeartRateModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </>
  );
}
